/**
 * Toast messages padronizados pra módulo de campanhas.
 * Segue o padrão de outros módulos (conciliation/*) com `Messages` global type.
 */

export const TOAST_VARIANT = {
  DEFAULT: "default",
  DESTRUCTIVE: "destructive",
} as const;

export const COMMON_TEXTS = {
  ALL_STORES: "Todas as lojas",
  BACK: "Voltar",
  CANCEL: "Cancelar",
  CONFIRM: "Confirmar",
  SUCCESS_TITLE: "Sucesso!",
  ADD_PRODUCT: "Incluir Produto",
  STORE_LABEL: (storeCode: number): string => `Loja ${storeCode}`,
  CAMPAIGN_LOAD_FAILED: "Não foi possível carregar a campanha. Tente novamente.",
  ITEM_CODES_SUFFIX: (codes: string): string => `Produtos: ${codes}.`,
  SHOWING: (from: number, to: number, total: number): string =>
    `Mostrando ${from}-${to} de ${total}`,
  PAGE: (page: number, total: number): string => `Página ${page} de ${total}`,
} as const;

/**
 * Toast standalone — usado quando nenhum campo foi alterado no modal de
 * edição. Não cabe na estrutura MessageType (que exige SUCCESS/ERROR), por
 * isso fica como export separado.
 */
export const NO_CHANGES_TOAST: Message = {
  variant: "default",
  description: "Nenhum campo foi modificado.",
  duration: 4000,
  closeOnClick: true,
};

export const MESSAGES: Messages = {
  CREATE: {
    SUCCESS: {
      variant: "default",
      description: "Campanha criada com sucesso!",
      duration: 5000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Erro ao criar campanha.",
      duration: 5000,
      closeOnClick: true,
    },
  },
  UPDATE: {
    SUCCESS: {
      variant: "default",
      description: "Campanha atualizada com sucesso!",
      duration: 5000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Erro ao atualizar campanha. Verifique os dados e tente novamente.",
      duration: 5000,
      closeOnClick: true,
    },
  },
  EXTEND: {
    SUCCESS: {
      variant: "default",
      description: "Data da campanha prorrogada com sucesso!",
      duration: 5000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Erro ao prorrogar campanha.",
      duration: 5000,
      closeOnClick: true,
    },
  },
  END: {
    SUCCESS: {
      variant: "default",
      description:
        "Encerramento registrado. A mudança pode levar até 48h para valer em todas as lojas.",
      duration: 6000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Erro ao encerrar campanha. Tente novamente.",
      duration: 5000,
      closeOnClick: true,
    },
  },
  TOGGLE_ITEM: {
    SUCCESS: {
      variant: "default",
      description: "Status do item alterado com sucesso!",
      duration: 4000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Erro ao alterar status do item.",
      duration: 5000,
      closeOnClick: true,
    },
  },
  ADD_ITEMS: {
    SUCCESS: {
      variant: "default",
      description: "Itens adicionados à lista com sucesso!",
      duration: 4000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Erro ao adicionar itens à lista.",
      duration: 5000,
      closeOnClick: true,
    },
  },
  CONFLICT: {
    SUCCESS: {
      variant: "default",
      description: "Operação concluída.",
      duration: 4000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Existem produtos em conflito com outras campanhas ativas.",
      duration: 6000,
      closeOnClick: true,
    },
  },
  RESOLVE_CONFLICT: {
    SUCCESS: {
      variant: "default",
      description:
        "Produto marcado para remoção da(s) campanha(s) anterior(es). A alteração será aplicada ao criar a campanha.",
      duration: 5000,
      closeOnClick: true,
    },
    INFO: {
      variant: "default",
      description: "Produto descartado desta campanha. Os demais permanecem.",
      duration: 4000,
      closeOnClick: true,
    },
    WARNING: {
      variant: "destructive",
      description:
        "Não foi possível remover o produto de todas as campanhas anteriores.",
      duration: 6000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Erro ao remover o produto da campanha anterior.",
      duration: 5000,
      closeOnClick: true,
    },
  },
  UPDATE_STORES: {
    SUCCESS: {
      variant: TOAST_VARIANT.DEFAULT,
      description: "Lojas da campanha atualizadas.",
      duration: 6000,
      closeOnClick: true,
    },
    ERROR: {
      variant: TOAST_VARIANT.DESTRUCTIVE,
      description: "Não foi possível atualizar as lojas da campanha. Tente novamente.",
      duration: 6000,
      closeOnClick: true,
    },
  },
  GRADE_RESTRICTION: {
    SUCCESS: {
      variant: TOAST_VARIANT.DEFAULT,
      description: "Restrição por cor/tamanho atualizada.",
      duration: 5000,
      closeOnClick: true,
    },
    ERROR: {
      variant: TOAST_VARIANT.DESTRUCTIVE,
      description: "Não foi possível alterar a restrição por cor/tamanho.",
      duration: 6000,
      closeOnClick: true,
    },
  },
  INVALID_ITEM: {
    SUCCESS: {
      variant: "default",
      description: "Operação concluída.",
      duration: 4000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Código do item inválido.",
      duration: 4000,
      closeOnClick: true,
    },
  },
};

export const ADD_ITEM_TEXTS = {
  TITLE: COMMON_TEXTS.ADD_PRODUCT,
  DESCRIPTION: (campaignCode: number): string =>
    `Busque itens no catálogo ITE001 para adicionar à campanha #${campaignCode}`,
  LIST_LABEL: "Lista",
  LIST_PLACEHOLDER: "Selecione a lista",
  LIST_OPTION: (listNumber: number): string => `Lista ${listNumber}`,
  SEARCH_LABEL: "Buscar Produto",
  SEARCH_PLACEHOLDER: "Digite código ou nome do produto (mín. 2 caracteres)",
  NO_RESULTS: "Nenhum item encontrado",
  SELECTED: (count: number): string => `Itens Selecionados (${count})`,
  REMOVE_SELECTED: (itemCode: string): string => `Remover ${itemCode} da seleção`,
  CONFLICT: (itemCode: string, itemName: string, description: string, code: number): string =>
    `Item ${itemCode} (${itemName}) já está na campanha "${description}" (#${code})`,
  CANCEL: COMMON_TEXTS.CANCEL,
  ADDING: "Adicionando...",
  ADD: (count: number): string => `Adicionar (${count})`,
  SUCCESS_TITLE: COMMON_TEXTS.SUCCESS_TITLE,
  SUCCESS: (count: number): string => `${count} produto(s) adicionado(s) com sucesso`,
  SUCCESS_WITH_SKIPPED: (count: number, codes: string): string =>
    `${count} produto(s) adicionado(s) com sucesso. Fora do cadastro, não entraram: ${codes}.`,
  ERROR_TITLE: "Erro ao adicionar itens",
  ERROR: "Erro inesperado. Tente novamente.",
} as const;

export const END_CAMPAIGN_LABELS = {
  ACTION: "Encerrar campanha",
  NOTICE: "O encerramento pode levar até 48h para valer em todas as lojas.",
  DATE_FIELD: "Data de encerramento",
  CURRENT_END: "Término atual:",
  CANCEL: "Cancelar",
} as const;
